import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../utils/cn'
import { disabledFieldProps } from './input'
import { Spinner } from './spinner'

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-surface-canvas aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        solid: 'bg-primary text-primary-foreground hover:bg-primary-hover',
        outline:
          'border border-line-emphasized bg-transparent text-content-primary hover:bg-surface-subtle',
        ghost:
          'bg-transparent text-content-secondary hover:bg-surface-subtle hover:text-content-primary',
        subtle: 'bg-surface-subtle text-content-primary hover:bg-surface-muted',
        link: 'h-auto bg-transparent p-0 text-content-primary underline-offset-4 hover:underline',
        unstyled: '',
      },
      size: {
        xs: 'h-7 px-2 text-xs',
        sm: 'h-8 px-3 text-sm',
        md: 'h-9 px-4 text-sm',
        lg: 'h-10 px-5 text-base',
      },
    },
    defaultVariants: { variant: 'solid', size: 'md' },
  },
)

type ColorScheme = 'gray' | 'accent' | 'brand' | 'red' | 'danger' | 'green' | 'success' | string

export interface ButtonProps
  extends
    Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
  loadingText?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  isDisabled?: boolean
  isActive?: boolean
  colorScheme?: ColorScheme
}

function colorSchemeClasses(
  colorScheme: ColorScheme | undefined,
  variant: ButtonProps['variant'],
): string {
  if (colorScheme === 'red' || colorScheme === 'danger') {
    if (variant === 'outline')
      return 'border-danger/60 text-danger hover:bg-danger-bg hover:text-danger'
    if (variant === 'ghost' || variant === 'link') return 'text-danger hover:bg-danger-bg'
    return 'bg-destructive text-destructive-foreground hover:bg-destructive-hover'
  }
  // Temporary: unify all non-destructive buttons on the canonical `--primary`
  // color. `colorScheme="green"/"success"` is intentionally a no-op for now so
  // primary CTAs render one consistent color instead of a green/black mix
  // (SolvaPay QA #10). Restore a green mapping here to reintroduce it.
  return ''
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant = 'solid',
      size = 'md',
      asChild = false,
      isLoading = false,
      loadingText,
      leftIcon,
      rightIcon,
      isDisabled,
      isActive,
      colorScheme,
      disabled,
      type,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    // Our <Button> defaults to type="button"; a native <button> defaults to
    // type="submit". The whole codebase relies on the type="button" default,
    // so in-form action buttons (e.g. a "Delete" that opens a confirm modal, or a
    // "Cancel") rely on NOT submitting. Restore that default so a real submit
    // button must opt in with an explicit type="submit". Don't force a type when
    // rendering asChild (the child element owns its own semantics).
    const resolvedType = asChild ? type : (type ?? 'button')
    const content = (
      <>
        {isLoading ? (
          <Spinner size="sm" className="text-current" />
        ) : (
          leftIcon && <span className="inline-flex shrink-0">{leftIcon}</span>
        )}
        {isLoading && loadingText ? loadingText : children}
        {!isLoading && rightIcon && <span className="inline-flex shrink-0">{rightIcon}</span>}
      </>
    )

    const isDisabledState = disabled || isDisabled || isLoading
    // Disabled state is driven by JS/CSS (aria-disabled + pointer/keyboard guards
    // + the `aria-disabled:` utility classes), never the native `disabled`
    // attribute — so behaviour is identical for a native <button> and an asChild
    // <a>/Link, and independent of per-browser disabled semantics. `disabledFieldProps`
    // removes it from the tab order and swallows pointer/keyboard activation.
    return (
      <Comp
        ref={ref}
        className={cn(
          buttonVariants({ variant, size }),
          colorSchemeClasses(colorScheme, variant),
          isActive && 'bg-surface-muted',
          className,
        )}
        aria-busy={isLoading || undefined}
        data-active={isActive ? '' : undefined}
        type={resolvedType}
        {...props}
        {...disabledFieldProps(isDisabledState)}
      >
        {asChild ? children : content}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export const ButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { isAttached?: boolean; spacing?: number }
>(({ className, isAttached, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'inline-flex items-center',
      isAttached
        ? // Render as one segmented control: no gaps, collapse the inner
          // corner radii and overlap adjacent borders so the buttons read as a
          // single attached group rather than separate pills.
          'gap-0 [&>*:not(:first-child)]:-ml-px [&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none'
        : 'gap-2',
      className,
    )}
    {...props}
  />
))
ButtonGroup.displayName = 'ButtonGroup'
